/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isDemoMode, displayCost, displayPersonaCount, displayToolCount } from '../../../utils/demoMode';

const DOMAIN_LABELS = {
  wms: 'WMS',
  iseries: 'iSeries',
  civil: 'Civil',
  'cross-domain': 'Cross-Domain',
};

function domainClass(domain) {
  const d = String(domain || '').toLowerCase();
  if (d === 'wms') return 'wf-card-domain wf-card-domain-wms';
  if (d === 'iseries') return 'wf-card-domain wf-card-domain-iseries';
  if (d === 'civil') return 'wf-card-domain wf-card-domain-civil';
  return 'wf-card-domain wf-card-domain-cross';
}

function stateClass(state) {
  const s = String(state || 'draft').toLowerCase();
  if (s === 'live') return 'wf-card-state wf-card-state-live';
  if (s === 'deprecated') return 'wf-card-state wf-card-state-deprecated';
  return 'wf-card-state wf-card-state-draft';
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

/**
 * Tolerates V2 schema drift — fields are read defensively.
 */
export default function WorkflowCard({ workflow }) {
  const navigate = useNavigate();
  const demo = isDemoMode();

  const id = workflow.id || workflow.workflow_id;
  const name = workflow.name || workflow.display_name || id;
  const domain = workflow.domain || 'cross-domain';
  // V2's `pricing` may be a string label OR an object {model, amount_usd}.
  // Route it through displayCost so an object never reaches JSX (React #31).
  const pricing = workflow.pricing_display
    || displayCost(workflow.pricing, demo)
    || (workflow.price_usd ? `$${workflow.price_usd.toLocaleString()} USD` : null);
  const description = workflow.description || workflow.summary || '';
  const stagesCount = Array.isArray(workflow.stages)
    ? workflow.stages.length
    : (workflow.stage_count || 5);
  const personasCount = Array.isArray(workflow.personas)
    ? workflow.personas.length
    : (workflow.persona_pool?.length || workflow.persona_count || 0);
  const toolsCount = Array.isArray(workflow.tools)
    ? workflow.tools.length
    : (workflow.tool_count || 0);
  const state = workflow.state || workflow.status || 'live';

  const onViewDetails = () => navigate(`/admin/workflows/${id}`);
  const onStartRun = () => navigate(`/admin/workflows/${id}#start`);

  return (
    <article className="wf-card" data-state={state}>
      <header className="wf-card-header">
        <h3 className="wf-card-title">{name}</h3>
        <div className="wf-card-badges">
          <span className={domainClass(domain)}>{DOMAIN_LABELS[domain] || domain}</span>
          {!demo && <span className={stateClass(state)}>{state}</span>}
        </div>
      </header>

      {pricing && !demo && (
        <div className="wf-card-pricing">{pricing}</div>
      )}

      <p className="wf-card-description">{truncate(description, 180)}</p>

      <div className="wf-card-stats">
        <span className="wf-card-stat" title="Stages">⛓ {stagesCount} stages</span>
        <span className="wf-card-stat" title="Personas">
          👤 {displayPersonaCount(personasCount, demo)}
        </span>
        <span className="wf-card-stat" title="Tools">
          🛠 {displayToolCount(toolsCount, demo)}
        </span>
      </div>

      <footer className="wf-card-footer">
        <button
          type="button"
          className="wf-btn wf-btn-secondary"
          onClick={onViewDetails}
        >
          View Details
        </button>
        <button
          type="button"
          className="wf-btn wf-btn-primary"
          onClick={onStartRun}
        >
          Start Run
        </button>
      </footer>
    </article>
  );
}
